/** SUNTRONIX ONE — ONE Preflight™ panel component.
 *
 *  Embed inside Deployment 360, Recovery 360, Shipment 360, Invoice 360,
 *  Asset 360 (redeployment).  Pass `entityKind` and `entityId` — the
 *  panel calls `POST /api/preflight/run` and renders checks grouped by
 *  category with the next-action button per failed check.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, ShieldAlert, ShieldX, ClipboardCheck, RefreshCw,
  CheckCircle2, AlertTriangle, Ban, HelpCircle, ChevronRight,
} from "lucide-react";

const RESULT_TONE = {
  pass:              { icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  warning:           { icon: AlertTriangle, cls: "text-amber-700 bg-amber-50 border-amber-200" },
  approval_required: { icon: ClipboardCheck, cls: "text-amber-800 bg-amber-100 border-amber-300" },
  blocking_failure:  { icon: Ban, cls: "text-red-800 bg-red-100 border-red-300" },
  not_applicable:    { icon: HelpCircle, cls: "text-slate-500 bg-slate-50 border-slate-200" },
  unable_to_verify:  { icon: AlertTriangle, cls: "text-orange-700 bg-orange-50 border-orange-200" },
};

const OVERALL_TONE = {
  ready:                     { label: "Ready to proceed",       tone: "bg-emerald-600 text-white",  icon: ShieldCheck },
  ready_with_warnings:       { label: "Ready with warnings",    tone: "bg-amber-500 text-white",    icon: ShieldAlert },
  approval_required:         { label: "Approval required",      tone: "bg-amber-600 text-white",    icon: ClipboardCheck },
  action_required_blocked:   { label: "Action required — blocked", tone: "bg-red-700 text-white",  icon: ShieldX },
};

const CATEGORY_LABEL = {
  customer_contract: "Customer & Contract",
  employee:          "Employee",
  technology_fit:    "Technology fit",
  asset_inventory:   "Asset & Inventory",
  device_management: "Device management",
  security:          "Security",
  address_logistics: "Address & Logistics",
  commercial:        "Commercial",
  documents:         "Documents",
  approvals:         "Approvals",
  exceptions:        "Exceptions",
};

export default function PreflightPanel({ entityKind, entityId, stage = "submit" }) {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/preflight/run",
        { entity_kind: entityKind, entity_id: entityId, stage });
      setRun(data.item);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preflight failed to run");
    } finally { setLoading(false); }
  }, [entityKind, entityId, stage]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    if (!run) return {};
    const g = {};
    (run.checks || []).forEach((c) => {
      const cat = c.category || "exceptions";
      g[cat] = g[cat] || [];
      g[cat].push(c);
    });
    return g;
  }, [run]);

  if (loading && !run) {
    return (
      <div className="st-panel p-4 text-[12px] text-slate-500"
            data-testid="preflight-loading">Running Preflight…</div>
    );
  }
  if (!run) return null;

  const overall = OVERALL_TONE[run.result_overall] || OVERALL_TONE.ready_with_warnings;
  const OIcon = overall.icon;
  const counts = run.counts || {};

  return (
    <div className="st-panel" data-testid="preflight-panel">
      {/* Header */}
      <div className={`flex items-center gap-3 rounded-t-lg px-4 py-3 ${overall.tone}`}>
        <OIcon className="h-5 w-5" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest opacity-80">One Preflight™</div>
          <div className="text-[15px] font-semibold">{overall.label}</div>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading}
          data-testid="preflight-rerun"
          className="text-white hover:bg-white/10">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
          Re-run
        </Button>
      </div>

      {/* Count strip */}
      <div className="grid grid-cols-6 border-b border-slate-100 text-center text-[11px]">
        {[
          ["pass", "Passed", "text-emerald-700"],
          ["warning", "Warnings", "text-amber-700"],
          ["approval_required", "Approvals", "text-amber-800"],
          ["blocking_failure", "Blocked", "text-red-700"],
          ["unable_to_verify", "Unverified", "text-orange-700"],
          ["not_applicable", "N/A", "text-slate-500"],
        ].map(([k, label, cls]) => (
          <div key={k} className="py-2" data-testid={`preflight-count-${k}`}>
            <div className={`text-lg font-semibold tabular-nums ${cls}`}>{counts[k] || 0}</div>
            <div className="uppercase tracking-wider text-[9px] text-slate-500">{label}</div>
          </div>
        ))}
      </div>

      {/* Checks grouped by category */}
      <div className="divide-y divide-slate-100">
        {Object.entries(grouped).map(([cat, checks]) => (
          <details key={cat} className="p-3" open={checks.some((c) =>
            ["blocking_failure","approval_required","warning","unable_to_verify"].includes(c.result))}
            data-testid={`preflight-cat-${cat}`}>
            <summary className="flex items-center gap-2 cursor-pointer">
              <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
              <span className="text-[10px] uppercase tracking-widest text-slate-600 font-semibold">
                {CATEGORY_LABEL[cat] || cat}
              </span>
              <span className="text-[10px] text-slate-400 ml-auto">{checks.length}</span>
            </summary>
            <ul className="mt-2 space-y-2">
              {checks.map((c) => {
                const tone = RESULT_TONE[c.result] || RESULT_TONE.not_applicable;
                const I = tone.icon;
                return (
                  <li key={c.rule_id}
                        className={`rounded border ${tone.cls} p-3 text-[12px]`}
                        data-testid={`preflight-check-${c.rule_id}`}>
                    <div className="flex items-start gap-2">
                      <I className="h-4 w-4 shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-semibold">{c.name}</div>
                        {c.reason && (
                          <div className="text-slate-700 mt-0.5">{c.reason}</div>
                        )}
                        {(c.current_value || c.required_value) && (
                          <div className="mt-1 grid grid-cols-2 gap-2 text-[11px]">
                            {c.current_value != null && (
                              <div>
                                <div className="uppercase tracking-wider text-[9px] text-slate-500">Current</div>
                                <div className="font-mono">{String(c.current_value)}</div>
                              </div>
                            )}
                            {c.required_value != null && (
                              <div>
                                <div className="uppercase tracking-wider text-[9px] text-slate-500">Required</div>
                                <div className="font-mono">{String(c.required_value)}</div>
                              </div>
                            )}
                          </div>
                        )}
                        {c.next_action && (
                          <div className="mt-2 text-[11px] font-medium">
                            Next: {c.next_action}
                          </div>
                        )}
                        {c.owner_email && (
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">
                            Owner · {c.owner_email}
                          </div>
                        )}
                        {c.evidence_required && (
                          <div className="text-[10px] text-slate-500 mt-1 italic">
                            Evidence: {c.evidence_required}
                          </div>
                        )}
                        {c.source && (
                          <div className="text-[10px] text-slate-400 mt-1">
                            Source · {c.source}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </details>
        ))}
      </div>

      <div className="px-3 py-2 border-t border-slate-100 text-[10px] text-slate-400 flex items-center justify-between">
        <span>Last checked · {new Date(run.run_at).toLocaleString()}</span>
        <span>Run · {run.id?.slice(0, 8)}</span>
      </div>
    </div>
  );
}
